import { useState } from "react";
import { useParams } from "react-router-dom";
import { Plus, Trash2, Target, Loader2 } from "lucide-react";
import api from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";

const parseList = (s) => {
  try { return JSON.parse(s || "[]"); } catch { return []; }
};

function Fact({ label, value, testid }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-sm mt-0.5" data-testid={testid}>{value || <span className="text-zinc-400">onbekend</span>}</div>
    </div>
  );
}

export const PvpMeasureSummary = ({ m, spec = {} }) => {
  const sfx = m.kwantiteit_eenheid === "Percentages" ? "%" : "";
  const fmt = (v) => (v !== undefined && v !== "" ? `${v}${sfx}` : "");
  const main = spec.hoofddoel_waarde && spec.hoofddoel_datum ? `${fmt(spec.hoofddoel_waarde)}${spec.hoofddoel_frequentie ? ` ${spec.hoofddoel_frequentie}` : ""} uiterlijk ${new Date(spec.hoofddoel_datum).toLocaleDateString("nl-NL")}` : "";
  return (
    <div className="border border-zinc-900 p-4 mb-6" data-testid="pvp-measure-summary">
      <div className="flex items-center gap-2 font-grotesk font-semibold mb-3"><Target className="h-4 w-4" /> Wat we gaan meten</div>
      <p className="text-sm text-zinc-700 mb-4" data-testid="pvp-measure-what">{m.wat || <span className="text-zinc-400">Nog niet ingevuld in het Meetplan.</span>}</p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <Fact label="Categorie" value={m.categorie} testid="pvp-measure-categorie" />
        <Fact label="Ondergrens" value={fmt(m.ondergrens)} testid="pvp-measure-ondergrens" />
        <Fact label="Bovengrens" value={fmt(m.bovengrens)} testid="pvp-measure-bovengrens" />
        <Fact label="Nulmeting" value={fmt(m.nulmeting_waarde)} testid="pvp-measure-nulmeting" />
        <Fact label="Hoofddoel" value={main} testid="pvp-measure-hoofddoel" />
      </div>
    </div>
  );
};

export const FREQS = ["per week", "per maand", "per jaar"];

export const PvpMainGoalField = ({ meetplan, values, disabled, onChange }) => {
  const [waarde, setWaarde] = useState("");
  const [datum, setDatum] = useState("");
  const [freq, setFreq] = useState("");
  const [busy, setBusy] = useState(false);
  const { id } = useParams();
  const { lo, hi, sfx, error } = bounds(meetplan, waarde);
  const set = (w, d, f, z = "") => onChange({ ...values, hoofddoel_waarde: w, hoofddoel_datum: d, hoofddoel_frequentie: f, hoofddoel_zin: z });
  const addGoal = async (w = waarde, d = datum, f = freq) => {
    setBusy(true);
    let zin = "";
    try {
      const { data } = await api.post(`/pvps/${id}/goal-sentence`, { waarde: w, datum: d, frequentie: f, gedrag: values.gedrag || "", eenheid: meetplan.kwantiteit_eenheid || "" });
      zin = data.sentence;
    } catch { zin = ""; }
    set(w, d, f, zin);
    setWaarde(""); setDatum(""); setFreq(""); setBusy(false);
  };
  const has = values.hoofddoel_waarde && values.hoofddoel_datum;

  return (
    <div className="space-y-3" data-testid="pvp-main-goal">
      <Label className="text-sm font-medium block">Hoofddoel: welke waarde moet wanneer bereikt zijn?</Label>
      {has ? (
        <div className="flex items-center gap-4 border-b border-zinc-200 py-2 text-sm" data-testid="pvp-main-goal-row">
          <span className="font-semibold w-40">{values.hoofddoel_waarde}{sfx} {values.hoofddoel_frequentie || ""}</span>
          <span className="text-zinc-600 flex-1">uiterlijk {new Date(values.hoofddoel_datum).toLocaleDateString("nl-NL")}</span>
          {!disabled && (
            <button type="button" onClick={() => set("", "", "")} className="text-zinc-400 hover:text-zinc-900" data-testid="pvp-main-goal-delete">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      ) : (
        <p className="text-sm text-zinc-400" data-testid="pvp-main-goal-empty">Nog geen hoofddoel gesteld.</p>
      )}
      {has && values.hoofddoel_zin && (
        <p className="font-grotesk text-lg leading-snug border-l-2 border-zinc-900 pl-3" data-testid="pvp-main-goal-sentence">{values.hoofddoel_zin}</p>
      )}
      {has && !values.hoofddoel_zin && !disabled && !busy && (
        <button type="button" onClick={() => addGoal(values.hoofddoel_waarde, values.hoofddoel_datum, values.hoofddoel_frequentie || "")}
          className="text-sm underline underline-offset-4 hover:text-zinc-600" data-testid="pvp-main-goal-sentence-generate">Formuleer hoofddoel in gewone taal</button>
      )}
      {busy && <p className="text-xs text-zinc-500 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Hoofddoel wordt in gewone taal geformuleerd…</p>}
      {!disabled && !has && (
        <>
          <div className="space-y-1">
            <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Frequentie</Label>
            <div className="flex gap-2">
              {FREQS.map((f) => (
                <button key={f} type="button" onClick={() => setFreq(f)}
                  className={`px-3 py-1.5 text-sm border transition-colors ${freq === f ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-400 hover:bg-zinc-100"}`}
                  data-testid={`pvp-main-goal-freq-${f.replace("per ", "")}`}>{f}</button>
              ))}
            </div>
          </div>
          <GoalInputs sfx={sfx} lo={lo} hi={hi} waarde={waarde} datum={datum} setWaarde={setWaarde} setDatum={setDatum}
            label={busy ? "Bezig…" : "Voeg hoofddoel toe"} prefix="pvp-main-goal" disabled={busy || !waarde || !datum || !freq || !!error}
            onAdd={() => addGoal()} />
        </>
      )}
      {error && <p className="text-sm text-red-600" data-testid="pvp-main-goal-error">{error}</p>}
    </div>
  );
};

const num = (v) => (v === "" || v === undefined || v === null ? null : Number(v));

function bounds(m, waarde, range) {
  const lo = range ? range[0] : num(m.ondergrens);
  const hi = range ? range[1] : num(m.bovengrens);
  const sfx = m.kwantiteit_eenheid === "Percentages" ? "%" : "";
  const n = Number(waarde);
  const error = waarde === "" ? "" : (lo !== null && n < lo) || (hi !== null && n > hi) ? `Kies een waarde tussen ${lo ?? "…"}${sfx} en ${hi ?? "…"}${sfx}.` : "";
  return { lo, hi, sfx, error };
}

function GoalInputs({ sfx, lo, hi, waarde, datum, setWaarde, setDatum, label, prefix, disabled, onAdd, maxDate }) {
  return (
    <div className="flex flex-wrap items-end gap-3 pt-2">
      <div className="space-y-1">
        <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Waarde{sfx && ` (${sfx})`}</Label>
        <Input type="number" min={lo ?? undefined} max={hi ?? undefined} value={waarde} onChange={(e) => setWaarde(e.target.value)} className="rounded-none border-zinc-400 w-36" data-testid={`${prefix}-value-input`} />
      </div>
      <div className="space-y-1">
        <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Te halen op</Label>
        <Input type="date" max={maxDate || undefined} value={datum} onChange={(e) => setDatum(e.target.value)} className="rounded-none border-zinc-400 w-44" data-testid={`${prefix}-date-input`} />
      </div>
      <Button type="button" onClick={onAdd} disabled={disabled} className="rounded-none" data-testid={`${prefix}-add-button`}>
        <Plus className="h-4 w-4 mr-1" /> {label}
      </Button>
    </div>
  );
}

const subgoalRange = (m, spec) => {
  const base = num(m.nulmeting_waarde) ?? num(m.ondergrens);
  const goal = num(spec.hoofddoel_waarde) ?? num(m.bovengrens);
  if (base === null || goal === null) return [base, goal];
  return [Math.min(base, goal), Math.max(base, goal)];
};

export const PvpSubgoalsField = ({ meetplan, spec = {}, values, disabled, onChange }) => {
  const list = parseList(values.doelen);
  const [waarde, setWaarde] = useState("");
  const [datum, setDatum] = useState("");
  const b = bounds(meetplan, waarde, subgoalRange(meetplan, spec));
  const { lo, hi, sfx } = b;
  const deadline = spec.hoofddoel_datum || "";
  const dateError = datum && deadline && datum >= deadline ? `Kies een datum vóór de hoofddoeldatum (${new Date(deadline).toLocaleDateString("nl-NL")}).` : "";
  const error = b.error || dateError;
  const hint = `Subdoelen liggen tussen ${num(meetplan.nulmeting_waarde) !== null ? "de nulmeting" : "de ondergrens"} en ${num(spec.hoofddoel_waarde) !== null ? "het hoofddoel" : "de bovengrens"}${deadline ? " en worden vóór de hoofddoeldatum gehaald" : ""}.`;
  const save = (next) => onChange({ ...values, doelen: JSON.stringify(next) });

  const add = () => {
    save([...list, { waarde, datum }].sort((a, b) => a.datum.localeCompare(b.datum)));
    setWaarde(""); setDatum("");
  };

  return (
    <div className="space-y-3 mb-8" data-testid="pvp-subgoals">
      <Label className="text-sm font-medium block">Subdoelen</Label>
      <p className="text-xs text-zinc-500" data-testid="pvp-subgoal-hint">{hint}</p>
      {list.length === 0 && <p className="text-sm text-zinc-400" data-testid="pvp-subgoals-empty">Nog geen subdoelen gesteld.</p>}
      {list.map((d, i) => (
        <div key={i} className="flex items-center gap-4 border-b border-zinc-200 py-2 text-sm" data-testid={`pvp-subgoal-${i}`}>
          <span className="font-mono text-xs text-zinc-400 w-6">{String(i + 1).padStart(2, "0")}</span>
          <span className="font-semibold w-28">{d.waarde}{sfx}</span>
          <span className="text-zinc-600 flex-1">uiterlijk {new Date(d.datum).toLocaleDateString("nl-NL")}</span>
          {!disabled && (
            <button type="button" onClick={() => save(list.filter((_, j) => j !== i))} className="text-zinc-400 hover:text-zinc-900" data-testid={`pvp-subgoal-delete-${i}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      ))}
      {!disabled && (
        <GoalInputs sfx={sfx} lo={lo} hi={hi} waarde={waarde} datum={datum} setWaarde={setWaarde} setDatum={setDatum}
          maxDate={deadline} label="Voeg subdoel toe" prefix="pvp-subgoal" disabled={!waarde || !datum || !!error} onAdd={add} />
      )}
      {error && <p className="text-sm text-red-600" data-testid="pvp-subgoal-error">{error}</p>}
    </div>
  );
};
